import React, { useState, useEffect } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "moment/locale/de"; // Import German locale
import "react-big-calendar/lib/css/react-big-calendar.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
  Box,
} from "@mui/material";
import { getToken, base_url } from "../../config/config";

// Set moment locale to German
moment.locale("de");

// Moment Localizer for the calendar
const localizer = momentLocalizer(moment);

// Helper function to fetch holidays
const fetchHolidays = async () => {
  try {
    const response = await fetch(`${base_url}/v1/getAllHolidays`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching holidays:", error);
    return [];
  }
};

// Helper function to check if a date is a weekend
const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
};

// Helper function to check if a date is a national holiday
const isNationalHoliday = (date, holidays) => {
  const dateStr = date.toISOString().split("T")[0]; // Compare only the date part
  return holidays.some((holiday) => {
    const holidayStr = new Date(holiday.holidayDate)
      .toISOString()
      .split("T")[0];
    return holidayStr === dateStr && holiday.holidayType === "National";
  });
};

// Define the addDaysSkippingWeekendsAndHolidays function
const addDaysSkippingWeekendsAndHolidays = (date, days, holidays) => {
  let result = new Date(date);
  while (days > 0) {
    result.setDate(result.getDate() + 1);

    // Only decrement days if it's neither a weekend nor a national holiday
    if (!isWeekend(result) && !isNationalHoliday(result, holidays)) {
      days--;
    }
  }
  return result;
};

// Function to remove duplicate events based on start, end, and title
const removeDuplicateEvents = (events) => {
  const uniqueEvents = events.filter(
    (event, index, self) =>
      index ===
      self.findIndex(
        (e) =>
          e.start.getTime() === event.start.getTime() &&
          e.end.getTime() === event.end.getTime() &&
          e.title === event.title,
      ),
  );
  return uniqueEvents;
};

const EmployeeShiftCalendar = () => {
  const [events, setEvents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false); // Single assignment modal
  const [multiModalIsOpen, setMultiModalIsOpen] = useState(false); // Multiple assignment modal
  const [selectedAssignments, setSelectedAssignments] = useState([]);
  const [selectedMultiEmployees, setSelectedMultiEmployees] = useState([]); // Multiple employee selection
  const [multiStartDate, setMultiStartDate] = useState(new Date());
  const [multiEndDate, setMultiEndDate] = useState(new Date());
  const [selectedMultiShifts, setSelectedMultiShifts] = useState([]); // Selected shifts for multi assignment
  const [holidays, setHolidays] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch employees, shifts, and holidays
        const [employeeData, shiftData, holidayData] = await Promise.all([
          fetch(`${base_url}/v1/getAllEmployees`).then((res) => res.json()),
          fetch(`${base_url}/v1/getShifts`).then((res) => res.json()),
          fetchHolidays(),
        ]);

        setEmployees(employeeData);
        setShifts(shiftData);
        setHolidays(holidayData);

        // Map employee shifts to calendar events while skipping national holidays
        const employeeShiftEvents = employeeData.flatMap((employee) =>
          employee.shifts.flatMap((shiftAssignment) => {
            const shift = shiftAssignment.shift;
            const shiftEvents = [];
            let currentDate = new Date(shiftAssignment.startDate);

            // Loop through the range of days between startDate and endDate
            while (currentDate <= new Date(shiftAssignment.endDate)) {
              // Skip weekends and national holidays
              if (
                !isWeekend(currentDate) &&
                !isNationalHoliday(currentDate, holidayData)
              ) {
                const startWork = new Date(currentDate);
                startWork.setHours(9, 0, 0); // Assuming 9 AM start time
                const endWork = new Date(currentDate);
                endWork.setHours(17, 0, 0); // Assuming 5 PM end time

                shiftEvents.push({
                  start: startWork,
                  end: endWork,
                  title: `${employee.name} - ${shift.name}`,
                  employeeName: employee.name, // Add employee name to event
                });
              }

              // Move to the next day, skipping weekends and national holidays
              currentDate = addDaysSkippingWeekendsAndHolidays(
                currentDate,
                1,
                holidayData,
              );
            }

            return shiftEvents;
          }),
        );

        // Remove duplicate events before setting them
        const uniqueEmployeeShiftEvents =
          removeDuplicateEvents(employeeShiftEvents);

        // Merge employee shift events with the existing events (don't replace)
        setEvents((prevEvents) =>
          removeDuplicateEvents([...prevEvents, ...uniqueEmployeeShiftEvents]),
        );
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  // Function to save single employee shifts to the backend
  const saveSingleShiftAssignment = async (selectedAssignments) => {
    try {
      const shiftAssignments = selectedAssignments.map((assignment) => ({
        employeeIds: [assignment.employeeId], // Make sure `employeeIds` is an array
        shiftIds: assignment.shifts,
        startDate: assignment.startDate,
        endDate: assignment.endDate,
      }));

      const response = await fetch(`${base_url}/v1/assignShifts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(shiftAssignments[0]), // Send the first item since it's for a single employee
      });

      const result = await response.json();
      if (response.ok) {
        alert("Shifts assigned and saved successfully!");
      } else {
        console.error(result.message);
        alert("Error assigning shifts: " + result.message);
      }
    } catch (error) {
      console.error("Error saving shifts:", error);
      alert("Failed to assign shifts due to network error");
    }
  };

  // Function to save multiple employee shifts to the backend
  const saveMultiShiftAssignment = async (
    selectedMultiEmployees,
    selectedMultiShifts,
    multiStartDate,
    multiEndDate,
  ) => {
    try {
      const multiShiftAssignments = selectedMultiEmployees.map(
        (employeeId) => ({
          employeeIds: [employeeId], // Wrap each ID in an array
          shiftIds: selectedMultiShifts,
          startDate: multiStartDate,
          endDate: multiEndDate,
        }),
      );

      const response = await fetch(`${base_url}/v1/assignShifts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(multiShiftAssignments), // Send the array for multiple employees
      });

      const result = await response.json();
      if (response.ok) {
        alert("Shifts assigned and saved successfully!");
      } else {
        console.error(result.message);
        alert("Error assigning shifts: " + result.message);
      }
    } catch (error) {
      console.error("Error saving shifts:", error);
      alert("Failed to assign shifts due to network error");
    }
  };

  // Handle form submission for single employee assignment
  const handleSubmit = async () => {
    const newEvents = selectedAssignments.flatMap((assignment) => {
      const employee = employees.find(
        (emp) => emp._id === assignment.employeeId,
      );
      return assignment.shifts.flatMap((shiftId) => {
        const shift = shifts.find((s) => s._id === shiftId);
        const shiftEvents = [];
        let currentDate = new Date(assignment.startDate);

        // Loop through the range of days between startDate and endDate
        while (currentDate <= new Date(assignment.endDate)) {
          // Check for weekends and national holidays
          if (
            !isWeekend(currentDate) &&
            !isNationalHoliday(currentDate, holidays)
          ) {
            const startWork = new Date(currentDate);
            startWork.setHours(9, 0, 0); // Assuming 9 AM start
            const endWork = new Date(currentDate);
            endWork.setHours(17, 0, 0); // Assuming 5 PM end

            shiftEvents.push({
              start: startWork,
              end: endWork,
              title: `${employee.name} - ${shift.name}`,
            });
          }

          // Move to the next working day, skipping weekends and national holidays
          currentDate = addDaysSkippingWeekendsAndHolidays(
            currentDate,
            1,
            holidays,
          );
        }

        return shiftEvents;
      });
    });

    const uniqueNewEvents = removeDuplicateEvents(newEvents);
    // Merge new unique events with the existing events
    setEvents((prevEvents) =>
      removeDuplicateEvents([...prevEvents, ...uniqueNewEvents]),
    );

    // Save the shift assignment to the backend
    await saveSingleShiftAssignment(selectedAssignments);

    setModalIsOpen(false);
  };

  // Handle form submission for multiple employee assignment
  const handleMultiSubmit = async () => {
    const newEvents = selectedMultiEmployees.flatMap((employeeId) => {
      const employee = employees.find((emp) => emp._id === employeeId);
      return selectedMultiShifts.flatMap((shiftId) => {
        const shift = shifts.find((s) => s._id === shiftId);
        const shiftEvents = [];
        let currentDate = new Date(multiStartDate);

        // Loop through the range of days between startDate and endDate
        while (currentDate <= new Date(multiEndDate)) {
          // Skip weekends and national holidays
          if (
            !isWeekend(currentDate) &&
            !isNationalHoliday(currentDate, holidays)
          ) {
            const startWork = new Date(currentDate);
            startWork.setHours(9, 0, 0); // Assuming 9 AM start
            const endWork = new Date(currentDate);
            endWork.setHours(17, 0, 0); // Assuming 5 PM end

            shiftEvents.push({
              start: startWork,
              end: endWork,
              title: `${employee.name} - ${shift.name}`,
            });
          }

          // Move to the next working day, skipping weekends and national holidays
          currentDate = addDaysSkippingWeekendsAndHolidays(
            currentDate,
            1,
            holidays,
          );
        }

        return shiftEvents;
      });
    });

    const uniqueNewEvents = removeDuplicateEvents(newEvents);
    // Merge new unique events with the existing events
    setEvents((prevEvents) =>
      removeDuplicateEvents([...prevEvents, ...uniqueNewEvents]),
    );

    // Save the multiple shift assignments to the backend
    await saveMultiShiftAssignment(
      selectedMultiEmployees,
      selectedMultiShifts,
      multiStartDate,
      multiEndDate,
    );

    setMultiModalIsOpen(false);
  };

  // Function to display holidays on the calendar
  useEffect(() => {
    const holidayEvents = holidays.map((holiday) => {
      const holidayDate = new Date(holiday.holidayDate);

      // For employee-specific holidays, show in red
      if (holiday.holidayType === "Employee") {
        return {
          start: holidayDate,
          end: holidayDate,
          title: `${holiday.employeeName} - ${holiday.employeeHolidayType}`,
          employeeHoliday: true, // Mark as employee holiday
        };
      }

      // For national holidays, show as a normal holiday
      return {
        start: holidayDate,
        end: holidayDate,
        title: holiday.holidayName,
      };
    });

    const uniqueHolidayEvents = removeDuplicateEvents(holidayEvents);
    // Merge holiday events with existing events (don't replace)
    setEvents((prevEvents) =>
      removeDuplicateEvents([...prevEvents, ...uniqueHolidayEvents]),
    );
  }, [holidays]);

  return (
    <div style={{ height: "100vh", padding: "20px" }}>
      <h1>Mitarbeiter-Schichtkalender</h1>

      <Button
        variant='contained'
        color='primary'
        onClick={() => setModalIsOpen(true)}
        style={{ marginBottom: "20px" }}>
        Shifts zuweisen (Einzelner Mitarbeiter)
      </Button>

      <Button
        variant='contained'
        color='secondary'
        onClick={() => setMultiModalIsOpen(true)}
        style={{ marginBottom: "20px", marginLeft: "10px" }}>
        Shifts zuweisen (Mehrere Mitarbeiter)
      </Button>

      {/* Single Employee Shift Assignment Modal */}
      <Dialog
        open={modalIsOpen}
        onClose={() => setModalIsOpen(false)}
        maxWidth='md'
        fullWidth>
        <DialogTitle>Schicht zuweisen (Einzelner Mitarbeiter)</DialogTitle>
        <DialogContent dividers>
          {employees.length === 0 ? (
            <Typography>Keine Mitarbeiter gefunden</Typography>
          ) : (
            employees.map((employee) => (
              <Box key={employee._id} mb={4}>
                <Typography variant='h6' gutterBottom>
                  {employee.name}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    {shifts.length === 0 ? (
                      <Typography>Keine Shifts gefunden</Typography>
                    ) : (
                      shifts.map((shift) => (
                        <FormControlLabel
                          key={shift._id}
                          control={
                            <Checkbox
                              onChange={(e) =>
                                setSelectedAssignments((prevAssignments) => {
                                  const updatedAssignments = [
                                    ...prevAssignments,
                                  ];
                                  const employeeIndex =
                                    updatedAssignments.findIndex(
                                      (a) => a.employeeId === employee._id,
                                    );
                                  if (employeeIndex >= 0) {
                                    if (e.target.checked) {
                                      updatedAssignments[
                                        employeeIndex
                                      ].shifts.push(shift._id);
                                    } else {
                                      updatedAssignments[employeeIndex].shifts =
                                        updatedAssignments[
                                          employeeIndex
                                        ].shifts.filter((s) => s !== shift._id);
                                    }
                                  } else {
                                    updatedAssignments.push({
                                      employeeId: employee._id,
                                      shifts: [shift._id],
                                      startDate: new Date(),
                                      endDate: new Date(),
                                    });
                                  }
                                  return updatedAssignments;
                                })
                              }
                            />
                          }
                          label={shift.name}
                        />
                      ))
                    )}
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2'>Startdatum:</Typography>
                    <DatePicker
                      selected={
                        selectedAssignments.find(
                          (a) => a.employeeId === employee._id,
                        )?.startDate || new Date()
                      }
                      onChange={(date) =>
                        setSelectedAssignments((prevAssignments) => {
                          const updatedAssignments = [...prevAssignments];
                          const employeeIndex = updatedAssignments.findIndex(
                            (a) => a.employeeId === employee._id,
                          );
                          if (employeeIndex >= 0) {
                            updatedAssignments[employeeIndex].startDate = date;
                          } else {
                            updatedAssignments.push({
                              employeeId: employee._id,
                              shifts: [],
                              startDate: date,
                              endDate: new Date(),
                            });
                          }
                          return updatedAssignments;
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2'>Enddatum:</Typography>
                    <DatePicker
                      selected={
                        selectedAssignments.find(
                          (a) => a.employeeId === employee._id,
                        )?.endDate || new Date()
                      }
                      onChange={(date) =>
                        setSelectedAssignments((prevAssignments) => {
                          const updatedAssignments = [...prevAssignments];
                          const employeeIndex = updatedAssignments.findIndex(
                            (a) => a.employeeId === employee._id,
                          );
                          if (employeeIndex >= 0) {
                            updatedAssignments[employeeIndex].endDate = date;
                          } else {
                            updatedAssignments.push({
                              employeeId: employee._id,
                              shifts: [],
                              startDate: new Date(),
                              endDate: date,
                            });
                          }
                          return updatedAssignments;
                        })
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button variant='contained' color='primary' onClick={handleSubmit}>
            Shifts zuweisen
          </Button>
          <Button variant='outlined' onClick={() => setModalIsOpen(false)}>
            Abbrechen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Multi Employee Shift Assignment Modal */}
      <Dialog
        open={multiModalIsOpen}
        onClose={() => setMultiModalIsOpen(false)}
        maxWidth='md'
        fullWidth>
        <DialogTitle>Shifts mehreren Mitarbeitern zuweisen</DialogTitle>
        <DialogContent dividers>
          <Typography variant='h6'>Mitarbeiter auswählen</Typography>
          <Grid container spacing={2}>
            {employees.length === 0 ? (
              <Typography>Keine Mitarbeiter gefunden</Typography>
            ) : (
              employees.map((employee) => (
                <Grid item xs={12} key={employee._id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={(e) =>
                          setSelectedMultiEmployees((prevSelected) => {
                            if (e.target.checked) {
                              return [...prevSelected, employee._id];
                            } else {
                              return prevSelected.filter(
                                (id) => id !== employee._id,
                              );
                            }
                          })
                        }
                      />
                    }
                    label={employee.name}
                  />
                </Grid>
              ))
            )}
          </Grid>

          <Typography variant='h6' style={{ marginTop: "20px" }}>
            Shifts auswählen
          </Typography>
          <Grid container spacing={2}>
            {shifts.length === 0 ? (
              <Typography>Keine Shifts gefunden</Typography>
            ) : (
              shifts.map((shift) => (
                <Grid item xs={12} key={shift._id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={(e) =>
                          setSelectedMultiShifts((prevSelected) => {
                            if (e.target.checked) {
                              return [...prevSelected, shift._id];
                            } else {
                              return prevSelected.filter(
                                (id) => id !== shift._id,
                              );
                            }
                          })
                        }
                      />
                    }
                    label={shift.name}
                  />
                </Grid>
              ))
            )}
          </Grid>

          <Grid container spacing={2} style={{ marginTop: "20px" }}>
            <Grid item xs={6}>
              <Typography variant='subtitle2'>Startdatum:</Typography>
              <DatePicker
                selected={multiStartDate}
                onChange={(date) => setMultiStartDate(date)}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant='subtitle2'>Enddatum:</Typography>
              <DatePicker
                selected={multiEndDate}
                onChange={(date) => setMultiEndDate(date)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant='contained'
            color='primary'
            onClick={handleMultiSubmit}>
            Shifts zuweisen
          </Button>
          <Button variant='outlined' onClick={() => setMultiModalIsOpen(false)}>
            Abbrechen
          </Button>
        </DialogActions>
      </Dialog>

      <Calendar
        localizer={localizer}
        events={events}
        startAccessor='start'
        endAccessor='end'
        style={{ height: "calc(100vh - 100px)" }}
        eventPropGetter={(event) => {
          // Set event color to red if it's an employee-specific holiday
          if (event.employeeHoliday) {
            return {
              style: {
                backgroundColor: "red",
                color: "white",
                borderRadius: "5px",
                padding: "5px",
              },
            };
          }

          // Otherwise, apply default styles
          return {
            style: {
              backgroundColor: event.title.includes(" - ")
                ? "#c8e6c9"
                : "#ffecb3", // Customize colors for shifts and holidays
              color: "black",
              borderRadius: "5px",
              padding: "5px",
            },
          };
        }}
      />
    </div>
  );
};

export default EmployeeShiftCalendar;
